import { reactive } from 'vue';
import { Module, ActionContext, Plugin } from 'vuex';
import cloneDeep from 'lodash-es/cloneDeep';
import instance, { claims } from '@growmark/cognito-vue';
import { GROWER } from "@growmark/gcmp-api/types";
import axios from 'axios';
import withConfig from '@growmark/axios/core';
import { bearer, versioned } from '@growmark/axios/config';
import { cognito } from "@growmark/axios/authn/cognito";

export const NAMESPACE = 'user';
export type State = typeof initial;
const initial = {
  email: <string | undefined>undefined,
  identity: <{ email: string } | undefined>undefined,
  must_change_password: false,
  must_verify_email: false,
  must_sign_up: false,
  must_sign_in: false,
  must_register: false,
  sso_redirect: {
    client_id: <string | undefined> undefined,
    redirect_uri: <string | undefined> undefined,
    response_type: <string | undefined> undefined,
    scope: <string | undefined> undefined,
    state: <string | undefined> undefined,
  },
  invite_redirect_url: <string | undefined> undefined,
  user: undefined,
};

const local = reactive({
  redirecting: false
});

export const plugin: Plugin<MinimalRootState> = async (store) => {
  try {
    const { email } = await claims;
    store.commit(`${NAMESPACE}/IDENTITY`, { email });
  } catch (err) {
    store.commit(`${NAMESPACE}/IDENTITY`, undefined);
  }
};

export type Actions = typeof actions;
type MinimalRootState = { [NAMESPACE]: State };
type UserActionContext = ActionContext<State, MinimalRootState>;
export const actions = {
  email: async ({ commit }: UserActionContext, email: string) => {
    commit('EMAIL', email);
  },
  identity: async ({ commit }: UserActionContext, identity: typeof initial.identity) => {
    commit('IDENTITY', identity);
  },
  must_change_password: async ({ commit }: UserActionContext, must_change_password: boolean) => {
    commit('MUST_CHANGE_PASSWORD', must_change_password);
  },
  must_verify_email: async ({ commit }: UserActionContext, must_verify_email: boolean) => {
    commit('MUST_VERIFY_EMAIL', must_verify_email);
  },
  must_sign_up: async ({ commit }: UserActionContext, must_sign_up: boolean) => {
    commit('MUST_SIGN_UP', must_sign_up);
  },
  must_sign_in: async ({ commit }: UserActionContext, must_sign_in: boolean) => {
    commit('MUST_SIGN_IN', must_sign_in);
  },
  must_register: async ({ commit }: UserActionContext, must_register: boolean) => {
    commit('MUST_REGISTER', must_register);
  },
  store_invite_redirect: async ({ commit }: UserActionContext, redirect: typeof initial.invite_redirect_url) => {
    commit('INVITE_REDIRECT', redirect);
  },
  store_sso_redirect: async ({ commit }: UserActionContext, redirect: typeof initial.sso_redirect) => {
    commit('SSO_REDIRECT', redirect);
  },
  execute_redirect: async ({ state, dispatch }: UserActionContext) => {
    local.redirecting = true;
    try {
      if (state.invite_redirect_url) {
        console.log("Invite Redirect URL");
        const redirect_url = new URL(state.invite_redirect_url);
        console.log("Redirecting To: ", redirect_url.toString());
        window.location.assign(redirect_url);
      } else if (state.must_register) {
        console.log("User must register");
        const redirect_url = new URL('/myfs', window.location.origin);
        console.log("Redirecting To: ", redirect_url.toString());
        dispatch('must_register', false);
        window.location.assign(redirect_url);
      } else if (state.sso_redirect.redirect_uri) {
        console.log("SSO Redirect URL");
        const api = withConfig<GROWER.ENDPOINTS>([versioned, bearer(cognito(instance, { type: 'access_token' }), { header: 'Authorization' })])(axios.create({
          baseURL: `${process.env.VUE_APP_SERVICE_API_URL}/${process.env.VUE_APP_SERVICE_API_VER}`,
          timeout: 30 * 1000
        }));
        const { code, lastError } = await (async () => {
          let code: string | undefined;
          let lastError: unknown;
          let attempt = 1;
          while (attempt <= 3) {
            try {
              const response = await api.put({ url: '/oauth2/token' });
              code = response.data;
              break;
            } catch (error) {
              lastError = error;
              if (attempt < 3) {
                await new Promise((resolve) => setTimeout(resolve, 1000));
              }
              attempt++;
            }
          }
          return { code, lastError };
        })();
        if (code === undefined && lastError) {
          throw lastError;
        }
        const redirect_url = new URL(state.sso_redirect.redirect_uri);
        if (state.sso_redirect.client_id) redirect_url.searchParams.set('client_id', state.sso_redirect.client_id);
        if (state.sso_redirect.response_type) redirect_url.searchParams.set('response_type', state.sso_redirect.response_type);
        if (state.sso_redirect.scope) redirect_url.searchParams.set('scope', state.sso_redirect.scope);
        if (state.sso_redirect.state) redirect_url.searchParams.set('state', state.sso_redirect.state);
        redirect_url.searchParams.set('code', code || '');
        console.log("Redirecting To: ", redirect_url.toString());
        window.location.assign(redirect_url);
      } else {
        console.log("No SSO Redirect Information Stored");
        const redirect_url = new URL('/myfs', window.location.origin);
        console.log("Redirecting To: ", redirect_url.toString());
        window.location.assign(redirect_url);
      }
    } catch (error) {
      local.redirecting = false;
      console.error(error);
      throw error;
    }
  }
};

export type Getters = typeof getters;
export const getters = {
  email: (state: State) => state.email,
  identity: (state: State) => state.identity,
  must_change_password: (state: State) => state.must_change_password,
  must_verify_email: (state: State) => state.must_verify_email,
  must_sign_up: (state: State) => state.must_sign_up,
  must_sign_in: (state: State) => state.must_sign_in,
  must_register: (state: State) => state.must_register,
  redirect_sso: (state: State) => state.sso_redirect,
  redirect_invite_url: (state: State) => state.invite_redirect_url,
  is_redirecting: () => local.redirecting
};

export type Mutations = typeof mutations;
export const mutations = {
  EMAIL: (state: State, email: string) => {
    state.email = email;
  },
  IDENTITY: (state: State, identity: typeof initial.identity) => {
    state.identity = identity;
  },
  MUST_CHANGE_PASSWORD: (state: State, must_change_password: boolean) => {
    state.must_change_password = must_change_password;
  },
  MUST_VERIFY_EMAIL: (state: State, must_verify_email: boolean) => {
    state.must_verify_email = must_verify_email;
  },
  MUST_SIGN_UP: (state: State, must_sign_up: boolean) => {
    state.must_sign_up = must_sign_up;
  },
  MUST_SIGN_IN: (state: State, must_sign_in: boolean) => {
    state.must_sign_in = must_sign_in;
  },
  MUST_REGISTER: (state: State, must_register: boolean) => {
    state.must_register = must_register;
  },
  SSO_REDIRECT: (state: State, redirect: typeof initial.sso_redirect) => {
    state.sso_redirect = redirect;
  },
  INVITE_REDIRECT: (state: State, invite_redirect_url: typeof initial.invite_redirect_url) => {
    state.invite_redirect_url = invite_redirect_url;
  }
};

export const module: Module<State, MinimalRootState> = {
  namespaced: true,
  state: cloneDeep(initial),
  getters,
  mutations,
  actions
};